import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { Room, Table } from '@shared/types';
import { Header } from '../components/layout/Header';
import { TableManager } from '../components/admin/TableManager';
import { WaitlistManager } from '../components/admin/WaitlistManager';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { ErrorBoundary } from '../components/common/ErrorBoundary';

export function AdminDashboard() {
  const { userProfile } = useAuth();
  const [selectedTable, setSelectedTable] = useState<Table | null>(null);
  const [selectedRoomId, setSelectedRoomId] = useState<string>('');
  const [rooms, setRooms] = useState<Room[]>([]);
  const [tables, setTables] = useState<Table[]>([]);
  const [roomsLoading, setRoomsLoading] = useState(true);
  const [tablesLoading, setTablesLoading] = useState(true);
  const [roomsError, setRoomsError] = useState<Error | null>(null);
  const [tablesError, setTablesError] = useState<Error | null>(null);
  
  // Get the current room for this admin
  // For superadmins, use selectedRoomId; for regular admins, use userProfile.roomId
  const currentRoomId = userProfile?.role === 'superadmin' ? selectedRoomId : userProfile?.roomId;

  // Direct Firebase approach for rooms (only for superadmins)
  useEffect(() => {
    if (userProfile?.role !== 'superadmin' || !db) {
      setRoomsLoading(false);
      return;
    }

    const roomsRef = collection(db, 'rooms');
    
    const unsubscribe = onSnapshot(
      roomsRef,
      (snapshot) => {
        const roomsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        })) as Room[];
        setRooms(roomsData);
        setRoomsLoading(false);
      },
      (err) => {
        setRoomsError(err as Error);
        setRoomsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [userProfile?.role]);

  // Set default selected room for superadmins when rooms load
  React.useEffect(() => {
    if (userProfile?.role === 'superadmin' && rooms && rooms.length > 0 && !selectedRoomId) {
      setSelectedRoomId(rooms[0]!.id);
    }
  }, [userProfile?.role, rooms, selectedRoomId]);

  // Direct Firebase approach for tables
  useEffect(() => {
    if (!currentRoomId || !db) {
      setTables([]);
      setTablesLoading(false);
      return;
    }

    const tablesRef = collection(db, 'rooms', currentRoomId, 'tables');
    
    const unsubscribe = onSnapshot(
      tablesRef,
      (snapshot) => {
        const tablesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        })) as Table[];
        setTables(tablesData);
        setTablesLoading(false);
      },
      (err) => {
        setTablesError(err as Error);
        setTablesLoading(false);
      }
    );

    return () => unsubscribe();
  }, [currentRoomId]);

  if (roomsLoading || tablesLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (roomsError || tablesError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Error</h1>
          <p className="text-gray-600">
            {roomsError?.message || tablesError?.message || 'Failed to load data'}
          </p>
        </div>
      </div>
    );
  }

  if (!currentRoomId) {
    if (userProfile?.role === 'superadmin') {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Select a Room</h1>
            <p className="text-gray-600">
              Please select a poker room from the dropdown above to manage its tables and waitlists.
            </p>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">No Room Assigned</h1>
          <p className="text-gray-600">
            You haven't been assigned to a poker room yet. Please contact a superadmin.
          </p>
        </div>
      </div>
    );
  }

  const currentRoom = (rooms as Room[])?.find((room: Room) => room.id === currentRoomId);

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-gray-50">
        <Header userProfile={userProfile} />
        
        <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          <div className="px-4 py-6 sm:px-0">
            {/* Room Selector for Superadmins */}
            {userProfile?.role === 'superadmin' && (
              <div className="mb-8">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Select Poker Room</h2>
                  <div className="max-w-md">
                    <label htmlFor="room-selector" className="block text-sm font-medium text-gray-700 mb-2">
                      Choose a room to manage:
                    </label>
                    <select
                      id="room-selector"
                      value={selectedRoomId}
                      onChange={(e) => setSelectedRoomId(e.target.value)}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                    >
                      <option value="">Select a room...</option>
                      {(rooms as Room[])?.map((room: Room) => (
                        <option key={room.id} value={room.id}>
                          {room.name} - {room.address}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Room Info */}
            <div className="mb-8">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">
                    {currentRoom?.name || 'Poker Room'} Admin Dashboard
                  </h1>
                  <p className="mt-2 text-gray-600">
                    Manage tables and waitlists for {currentRoom?.name || 'your poker room'}
                  </p>
                </div>
                <div className="flex flex-col space-y-2">
                  <button
                    onClick={() => window.open(`/tv-dashboard?roomId=${currentRoomId}`, '_blank')}
                    className="btn btn-primary flex items-center space-x-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <span>Open TV Dashboard</span>
                  </button>
                  <button
                    onClick={() => window.open(`/tv-dashboard-settings?roomId=${currentRoomId}`, '_blank')}
                    className="btn btn-secondary flex items-center space-x-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span>TV Dashboard Settings</span>
                  </button>
                  <button
                    onClick={() => window.open(`/scheduler?roomId=${currentRoomId}`, '_blank')}
                    className="btn btn-secondary flex items-center space-x-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span>Open Scheduler</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Table Management */}
            <div className="mb-8">
              <TableManager
                roomId={currentRoomId}
                tables={(tables as Table[] || []).sort((a, b) => (a.display_order || 999) - (b.display_order || 999))}
                onTableSelect={setSelectedTable}
                selectedTable={selectedTable}
              />
            </div>

            {/* Waitlist Management */}
            {selectedTable && (
              <div>
                <WaitlistManager
                  roomId={currentRoomId}
                  table={selectedTable}
                  onClose={() => setSelectedTable(null)}
                />
              </div>
            )}
          </div>
        </main>

      </div>
    </ErrorBoundary>
  );
}
